import {
  useEffect, useState, useContext,
} from 'react';
import { Query } from '../../../utils/query';
import { Order } from '../../../utils/table';
import { SearchSortContext } from '../../../context/SearchSortContext';
import { ChangeSearchPayload, PageSearchSortContextActionType } from '../../../context/types';
import parseUrlFilterSearch from '../../../hooks/parseUrlFilterSearch';
import useUrlFilterSearch from '../../../hooks/useUrlFilterSearch';
import { UrlParam } from '../../../redux/types/filter';
import { MarketReport } from '../../../redux/types/reports';

interface UrlParamProperties extends UrlParam {
  builderId?: string | number | null
  startDate?: Date | null
  endDate?: Date | null
}

const useMarketReport = (loadMarketReport: (query: Query) => void) => {
  const urlParse = parseUrlFilterSearch<UrlParamProperties>();
  const { dispatch, state: { reports } } = useContext(SearchSortContext);
  const {
    currentOrderBy, currentOrder, currentPage, rowsPerPageAmount, contextSearch,
  } = reports;

  const [page, setPage] = useState<number>(urlParse.page || currentPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(urlParse.limit || rowsPerPageAmount);
  const [search, setSearch] = useState<string>(urlParse.search || contextSearch);
  const [order, setOrder] = useState<Order>(urlParse.order || currentOrder);
  const [orderBy, setOrderBy] = useState<keyof MarketReport>(urlParse.orderBy || currentOrderBy);
  const [builderId, setBuilderId] = useState<string | number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useUrlFilterSearch<UrlParamProperties>({
    search, order, orderBy, page, limit: rowsPerPage, builderId, startDate, endDate,
  });

  const dispatchMarketReport = (
    type: PageSearchSortContextActionType<'CHANGE_MARKET_REPORT'>,
    payload?: ChangeSearchPayload<MarketReport>,
  ) => {
    dispatch({ type, payload: { ...reports, ...payload } });
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    loadMarketReport({
      search, order, orderBy, page, limit: rowsPerPage, builderId, startDate, endDate,
    });
  }, [order, orderBy, page, rowsPerPage, search,
    loadMarketReport, builderId, startDate, endDate]);

  const searchRequest = () => loadMarketReport({
    search, order, orderBy, page, limit: rowsPerPage, startDate, endDate,
  });

  return {
    order,
    orderBy,
    page,
    search,
    rowsPerPage,
    builderId,
    startDate,
    endDate,
    setSearch,
    setOrder,
    setOrderBy,
    setBuilderId,
    setStartDate,
    setEndDate,
    setPage,
    setRowsPerPage,
    dispatchMarketReport,
    searchRequest,
  };
};

export default useMarketReport;
